/**
 * Analytics Page (Intelligence)
 * Performance metrics and trends
 */
import React from 'react';
import { 
  BarChart3, 
  TrendingUp, 
  TrendingDown,
  Target,
  Leaf,
  Factory,
  Zap,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';
import { JourneySection, JourneyPanel } from '../journeys/JourneyPanel';
import { TargetTracking } from '../journeys/JourneyIntelligence';

const METRICS = [
  {
    id: 1,
    name: 'Total GHG Emissions',
    value: '156,230',
    unit: 'tCO2e',
    change: -8.5,
    trend: 'down',
    target: '145,000',
    status: 'on-track',
  },
  {
    id: 2,
    name: 'Renewable Energy',
    value: '67',
    unit: '%',
    change: 12.3,
    trend: 'up',
    target: '75',
    status: 'on-track',
  },
  {
    id: 3,
    name: 'Supplier ESG Coverage',
    value: '87',
    unit: '%',
    change: 5.2,
    trend: 'up',
    target: '95',
    status: 'attention',
  },
  {
    id: 4,
    name: 'Compliance Score',
    value: '92',
    unit: '%',
    change: 3.1,
    trend: 'up',
    target: '95',
    status: 'on-track',
  },
];

const TREND_ICON = {
  up: { icon: TrendingUp, color: 'text-emerald-400' },
  down: { icon: TrendingDown, color: 'text-red-400' },
};

export default function AnalyticsPage() {
  return (
    <div className="space-y-6" data-testid="analytics-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Analytics</h1>
        <p className="text-sm text-[#6B6B6B] mt-1">
          Track performance metrics, trends, and progress against targets
        </p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {METRICS.map(metric => {
          const trendConfig = TREND_ICON[metric.trend];
          const TrendIcon = trendConfig.icon;
          const isPositive = (metric.trend === 'up' && metric.name !== 'Total GHG Emissions') ||
                           (metric.trend === 'down' && metric.name === 'Total GHG Emissions');
          
          return (
            <div 
              key={metric.id} 
              className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4"
              data-testid={`metric-card-${metric.id}`}
            >
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs text-[#6B6B6B] uppercase tracking-wider">{metric.name}</span>
                <span className={`flex items-center gap-1 text-xs ${isPositive ? 'text-emerald-400' : 'text-red-400'}`}>
                  {isPositive ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                  {Math.abs(metric.change)}%
                </span>
              </div>
              <div className="flex items-baseline gap-2 mb-3">
                <span className="text-3xl font-bold text-white">{metric.value}</span>
                <span className="text-sm text-[#6B6B6B]">{metric.unit}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-[#6B6B6B]">Target: {metric.target}{metric.unit === '%' ? '%' : ` ${metric.unit}`}</span>
                <span className={`px-2 py-0.5 rounded ${
                  metric.status === 'on-track' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'
                }`}>
                  {metric.status === 'on-track' ? 'On Track' : 'Attention'}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Charts Placeholder */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-5">
          <h3 className="text-base font-semibold text-white mb-4">Emissions Trend</h3>
          <div className="h-64 flex items-center justify-center bg-[#0A0A0A] rounded-lg">
            <div className="text-center">
              <BarChart3 size={48} className="mx-auto text-[#2F2F2F] mb-2" />
              <p className="text-sm text-[#6B6B6B]">Chart visualization coming soon</p>
            </div>
          </div>
        </div>
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-5">
          <h3 className="text-base font-semibold text-white mb-4">Framework Compliance</h3>
          <div className="h-64 flex items-center justify-center bg-[#0A0A0A] rounded-lg">
            <div className="text-center">
              <Target size={48} className="mx-auto text-[#2F2F2F] mb-2" />
              <p className="text-sm text-[#6B6B6B]">Chart visualization coming soon</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-emerald-500/10 to-transparent border border-emerald-500/20 rounded-xl p-5">
          <div className="flex items-center gap-3 mb-2">
            <Leaf size={20} className="text-emerald-400" />
            <span className="text-sm font-medium text-white">Carbon Reduction</span>
          </div>
          <div className="text-3xl font-bold text-emerald-400 mb-1">-15.2%</div>
          <p className="text-xs text-[#6B6B6B]">vs. baseline year 2019</p>
        </div>
        <div className="bg-gradient-to-br from-blue-500/10 to-transparent border border-blue-500/20 rounded-xl p-5">
          <div className="flex items-center gap-3 mb-2">
            <Factory size={20} className="text-blue-400" />
            <span className="text-sm font-medium text-white">Supplier Assessment</span>
          </div>
          <div className="text-3xl font-bold text-blue-400 mb-1">245</div>
          <p className="text-xs text-[#6B6B6B]">suppliers assessed this year</p>
        </div>
        <div className="bg-gradient-to-br from-purple-500/10 to-transparent border border-purple-500/20 rounded-xl p-5">
          <div className="flex items-center gap-3 mb-2">
            <Zap size={20} className="text-purple-400" />
            <span className="text-sm font-medium text-white">Energy Efficiency</span>
          </div>
          <div className="text-3xl font-bold text-purple-400 mb-1">+8.7%</div>
          <p className="text-xs text-[#6B6B6B]">improvement year over year</p>
        </div>
      </div>

      {/* Live User Journeys */}
      <JourneySection title="Live Journeys">
        <JourneyPanel title="SBTi Target Tracking" ucId="UC-29" defaultOpen><TargetTracking /></JourneyPanel>
      </JourneySection>
    </div>
  );
}
